const ROOT_ELEMENT_ID = "root";
const NG_STORAGE_KEY = "nijiurachan-ng-settings";
const LEGACY_IMAGE_NG_STORAGE_KEY = "futaba_image_ng";
const LEGACY_TEXT_NG_STORAGE_KEY = "futaba_ng_settings";
const NG_STORE_VERSION = 3;
const NG_ARRAY_KEYS = [
  "hiddenThreadIds",
  "ngDisplayIds",
  "ngTitles",
  "ngWords",
  "ngRegexes",
  "ngImages",
] as const;

type ReadableStorage = Pick<Storage, "getItem">;

/** Render a dependency-free startup error when React cannot be loaded safely. */
export function renderBootstrapFailure(
  documentLike: Document = globalThis.document,
  reload: () => void = () => globalThis.location.reload(),
  storage?: ReadableStorage,
): void {
  const root = documentLike.getElementById(ROOT_ELEMENT_ID);
  if (root === null) {
    return;
  }

  // Bootstrap cannot depend on the SPA's shared chunks. Its initial main may
  // still be in root, or app startup may already have moved it into a region.
  const initialThread = documentLike.querySelector<HTMLElement>(
    "main[data-aimg-thread-initial], section[data-aimg-thread-initial]",
  );
  if (initialThread) {
    if (canShowInitialThreadHtml(documentLike, storage)) {
      initialThread.removeAttribute("hidden");
      initialThread.setAttribute("role", "main");
      root.before(initialThread);
    } else {
      initialThread.remove();
    }
  }

  const alert = documentLike.createElement("div");
  alert.setAttribute("role", "alert");
  alert.setAttribute("aria-live", "assertive");

  const heading = documentLike.createElement("h1");
  heading.textContent = "ページを読み込めませんでした";

  const message = documentLike.createElement("p");
  message.textContent =
    "通信状態を確認して、しばらく待ってからもう一度お試しください。";

  const retry = documentLike.createElement("button");
  retry.type = "button";
  retry.textContent = "再読み込み";
  retry.addEventListener("click", reload);

  alert.append(heading, message, retry);
  root.replaceChildren(alert);
}

function canShowInitialThreadHtml(
  documentLike: Document,
  suppliedStorage?: ReadableStorage,
): boolean {
  try {
    const storage = suppliedStorage ?? documentLike.defaultView?.localStorage;
    if (storage === undefined) return false;

    const persisted = storage.getItem(NG_STORAGE_KEY);
    if (persisted === null) {
      return (
        storage.getItem(LEGACY_IMAGE_NG_STORAGE_KEY) === null &&
        storage.getItem(LEGACY_TEXT_NG_STORAGE_KEY) === null
      );
    }

    const parsed: unknown = JSON.parse(persisted);
    if (!isRecord(parsed) || parsed.version !== NG_STORE_VERSION) return false;
    const state = parsed.state;
    if (!isRecord(state)) return false;
    if (
      typeof state.enabled !== "boolean" ||
      typeof state.showNgContent !== "boolean"
    ) {
      return false;
    }
    if (!state.enabled || state.showNgContent) return true;

    if (
      NG_ARRAY_KEYS.some(
        (key) => !Array.isArray(state[key]) || state[key].length > 0,
      ) ||
      typeof state.legacyImageNgMigrated !== "boolean" ||
      typeof state.legacyTextNgMigrated !== "boolean"
    ) {
      return false;
    }
    if (
      (!state.legacyImageNgMigrated &&
        storage.getItem(LEGACY_IMAGE_NG_STORAGE_KEY) !== null) ||
      (!state.legacyTextNgMigrated &&
        storage.getItem(LEGACY_TEXT_NG_STORAGE_KEY) !== null)
    ) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
